import React, { useId, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Input, InputProps } from '@epoch-ui/components/Input';

import {
  FormField,
  FormControl,
  FormDescription,
  FormLabel,
  FormItem,
  FormMessage
} from '../../form';

export interface DebouncedInputFieldProps extends InputProps {
  label?: string | React.ReactNode | React.ReactNode[];
  description?: string | React.ReactNode | React.ReactNode[];
  inputClassName?: string;
  debounce?: number;
}

/**
 * DebouncedInputField - A form input field with debounced onChange callback
 *
 * Usage:
 * ```tsx
 * <DebouncedInputField
 *   name="channelName"
 *   label="Channel Name"
 *   debounce={500}
 *   onChange={(e) => {
 *     // This function will be called after the debounce delay
 *     // The form field value is automatically updated immediately
 *     // Use this for API calls, validation, etc.
 *     console.log('Debounced value:', e.target.value);
 *   }}
 * />
 * ```
 *
 * Key Points:
 * - Form field value updates immediately (normal form behavior)
 * - onChange callback is debounced and called after the specified delay
 * - No need to manually update form state in onChange - it's handled automatically
 * - Perfect for API calls, search, validation that shouldn't run on every keystroke
 */

export const DebouncedInputField = React.forwardRef<HTMLInputElement, DebouncedInputFieldProps>(
  (
    { name, label, description, className, inputClassName, debounce = 300, onChange, ...props },
    ref
  ) => {
    const formCtx = useFormContext();
    const id = useId();
    const fieldName = name || id;

    // Ref to store timeout ID for cleanup
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup timeout on unmount
    useEffect(() => {
      return (): void => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    // Debounced onChange handler
    const handleDebouncedOnChange = (value: string): void => {
      if (!onChange) return;

      // Clear previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        onChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
      }, debounce);
    };

    return (
      <FormField
        control={formCtx.control}
        name={fieldName}
        render={({ field, fieldState }): React.ReactElement => {
          // Handle input changes
          const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
            // 1. Update the form field normally (immediate)
            field.onChange(e);

            // 2. Call our debounced function
            handleDebouncedOnChange(e.target.value);
          };

          return (
            <FormItem className={cn('tw-mt-4', className)}>
              {label && <FormLabel className="tw-mb-0">{label}</FormLabel>}
              {description && <FormDescription className="tw-m-0">{description}</FormDescription>}
              <FormControl>
                <Input
                  {...props}
                  {...field}
                  value={field.value || ''}
                  onChange={handleInputChange}
                  className={cn('tw-mt-2', inputClassName)}
                  error={!!fieldState.error}
                  ref={ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  }
);

DebouncedInputField.displayName = 'DebouncedInputField';
